import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslationService } from '@/layout/service/translation.service';
import { RoomAvailabilityLine, RoomStatus } from '@/cloud/models/room-usage.models';

/** Colour family of a room status: in use, needs a look (amber), about to change, available, or no signal. */
export type RoomStatusTone = 'busy' | 'warning' | 'soon' | 'available' | 'neutral';

export const ROOM_STATUS_TONE: Record<RoomStatus, RoomStatusTone> = {
    inCall: 'busy',
    inMeeting: 'busy',
    bookedVacant: 'warning',
    occupied: 'busy',
    startingSoon: 'soon',
    vacant: 'available',
    free: 'available',
    notInCall: 'neutral',
    unknown: 'neutral',
    noData: 'neutral'
};

export const ROOM_STATUS_ICON: Record<RoomStatus, string> = {
    inCall: 'fa fa-video',
    inMeeting: 'fa fa-calendar-check',
    bookedVacant: 'fa fa-calendar-xmark',
    occupied: 'fa fa-user-group',
    startingSoon: 'fa fa-clock',
    vacant: 'fa fa-door-open',
    free: 'fa fa-calendar',
    notInCall: 'fa fa-video-slash',
    unknown: 'fa fa-circle-question',
    noData: 'fa fa-circle-minus'
};

/**
 * One line of text for a room's calendar ("In a meeting until 11:00 · Team Standup", "Booked until
 * 11:00", "Free until 14:00"...), for places that render text rather than markup (list cells, the
 * attention list). `time` formats an ISO timestamp.
 */
export function availabilityText(line: RoomAvailabilityLine | null, t: (key: string) => string, time: (iso: string) => string): string | null {
    if (!line) return null;
    const title = 'title' in line && line.title ? ` · ${line.title}` : '';
    switch (line.kind) {
        case 'inMeeting':
            return `${t('roomUsage.inMeetingUntil')} ${time(line.time)}${title}`;
        case 'bookedUntil':
            return `${t('roomUsage.bookedUntil')} ${time(line.time)}${title}`;
        case 'nextAt':
            return `${t('roomUsage.nextAt')} ${time(line.time)}${title}`;
        case 'freeUntil':
            return `${t('roomUsage.freeUntil')} ${time(line.time)}`;
        case 'freeRestOfDay':
            return t('roomUsage.freeRestOfDay');
        default:
            return t('roomUsage.availability.Unknown');
    }
}

/**
 * The single status pill of a room (see roomStatus for the precedence). Amber for booked but
 * vacant; muted grey when the state is stale (the control system is offline).
 */
@Component({
    selector: 'app-room-status-pill',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `<i [class]="icon()" aria-hidden="true"></i><span class="room-status-pill__label">{{ label() || t()('roomStatus.' + status()) }}</span>`,
    styles: [
        `
            :host {
                --pill-fg: var(--app-status-unknown-fg);
                --pill-bg: var(--app-status-unknown-bg);
                display: inline-flex;
                align-items: center;
                gap: 0.375rem;
                max-width: 100%;
                padding: 0.125rem 0.55rem;
                border-radius: 999px;
                color: var(--pill-fg);
                background: var(--pill-bg);
                font-size: 0.75rem;
                font-weight: 600;
                line-height: 1.5;
                white-space: nowrap;
            }
            :host([data-tone='busy']) {
                --pill-fg: var(--app-status-pending-fg);
                --pill-bg: var(--app-status-pending-bg);
            }
            :host([data-tone='warning']) {
                --pill-fg: var(--app-status-warning-fg);
                --pill-bg: var(--app-status-warning-bg);
            }
            :host([data-tone='soon']) {
                /* Outlined sky, so "starting soon" never reads as the amber "booked but vacant". */
                --pill-fg: var(--app-status-pending-fg);
                --pill-bg: transparent;
                box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--app-status-pending) 60%, transparent);
            }
            :host([data-tone='available']) {
                --pill-fg: var(--app-status-online-fg);
                --pill-bg: var(--app-status-online-bg);
            }
            :host(.room-status-pill--stale) {
                --pill-fg: var(--app-status-unknown-fg);
                --pill-bg: var(--app-status-unknown-bg);
            }
            .room-status-pill__label {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        `
    ],
    host: {
        class: 'room-status-pill',
        '[attr.data-status]': 'status()',
        '[attr.data-tone]': 'tone()',
        '[class.room-status-pill--stale]': 'stale()'
    }
})
export class RoomStatusPillComponent {
    readonly t = inject(TranslationService).t;

    readonly status = input.required<RoomStatus>();
    /** Last reported rather than live: drawn muted. */
    readonly stale = input(false);
    /** Replaces the status label (the attention list names the reason instead). */
    readonly label = input<string | null>(null);

    readonly tone = computed(() => ROOM_STATUS_TONE[this.status()] ?? 'neutral');
    readonly icon = computed(() => ROOM_STATUS_ICON[this.status()] ?? ROOM_STATUS_ICON.unknown);
}
